/**
 * Transforms between the builder's simplified schema and the proper API schema.
 *
 * Builder format: pages[].questions[] with label:string, options:string[]
 * Proper format:  pages[].blocks[] with label:{[lang]:string}, options:QuestionOption[]
 *
 * The builder only edits a subset of the persisted schema. Everything it does
 * not model (quiz answers, option images, other-language texts, unknown theme
 * tokens, timestamps, …) is carried through in `persisted` fields so a
 * properToBuilder → builderToProper round trip is lossless.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

import type { ConditionalLogic } from '@responsedock/types';
import type { RichTextFormat } from '@responsedock/utils';

export interface BuilderTheme {
  primaryColor?: string;
  primaryTextColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  mutedColor?: string;
  borderColor?: string;
  errorColor?: string;
  fontFamily?: string;
  fontUrl?: string;
  borderRadius?: string;
  buttonStyle?: string;
  optionStyle?: string;
  cardShadow?: string;
  questionSize?: 'small' | 'medium' | 'large';
  surveyWidth?: 'narrow' | 'standard' | 'wide';
  backgroundImage?: string;
  bannerImageUrl?: string;
  bannerImageAlt?: string;
  logoUrl?: string;
  brandName?: string;
  hidePlatformBranding?: boolean;
  welcomeEnabled?: boolean;
  welcomeTitle?: string;
  welcomeMessage?: string;
  welcomeMessageFormat?: RichTextFormat;
  welcomeButtonLabel?: string;
  welcomeImageUrl?: string;
  welcomeShowEstimatedTime?: boolean;
}

export interface BuilderQuestionConfig {
  options?: Array<string | { label: string; value?: string; imageUrl?: string; imageAlt?: string }>;
  min?: number;
  max?: number;
  step?: number;
  minLabel?: string;
  maxLabel?: string;
  npsLowLabel?: string;
  npsHighLabel?: string;
  placeholder?: string;
  allowOther?: boolean;
  imageUrl?: string;
  imageAlt?: string;
  maxFiles?: number;
  maxFileSizeMB?: number;
  acceptedFileTypes?: string[];
  /** Date questions (passed through unchanged): `YYYY-MM-DD` or `today`; `includeTime` stores `YYYY-MM-DDTHH:mm`. */
  minDate?: string;
  maxDate?: string;
  includeTime?: boolean;
  confidential?: boolean;
  [key: string]: unknown;
}

/**
 * The persisted objects a builder question was loaded from. builderToProper
 * merges builder edits over them so fields the builder does not edit survive.
 */
export interface BuilderPersistedQuestion {
  /** The original block, without its `question`. */
  block?: Record<string, unknown>;
  /** The original question object. */
  question?: Record<string, unknown>;
  /** The original options, aligned with `config.options` at load time. */
  options?: Record<string, unknown>[];
}

export interface BuilderQuestion {
  id: string;
  type: string;
  order: number;
  label: string;
  required: boolean;
  config: BuilderQuestionConfig;
  conditionalLogic?: ConditionalLogic | null;
  /** Default-language help text shown under the label. */
  description?: string;
  /** Quiz: expected answer (option value(s), or text graded by AI). */
  correctAnswer?: unknown;
  /** Quiz: points awarded for a correct answer. */
  points?: number;
  /** Quiz: explanation shown after answering. */
  explanation?: Record<string, string> | string | null;
  /** Per-language overrides: { fr: { label: '...', options: ['...'] } } */
  translations?: Record<string, { label?: string; options?: string[] }>;
  /** Persisted source objects (not edited by the builder UI). */
  persisted?: BuilderPersistedQuestion;
}

export interface BuilderPage {
  id: string;
  order: number;
  title: string;
  /** Default-language page description. */
  description?: string;
  questions: BuilderQuestion[];
  /** The original persisted page, without its blocks. */
  persisted?: Record<string, unknown>;
}

export interface BuilderFollowUpField {
  id: string;
  type: 'name' | 'email' | 'phone';
  label: string;
  required: boolean;
}

export interface BuilderFollowUpConfig {
  enabled: boolean;
  title: string;
  description: string;
  fields: BuilderFollowUpField[];
}

export interface BuilderGiftConfig {
  enabled: boolean;
  type: 'single' | 'random';
  codes: string[];
  label?: string;
}

export interface BuilderThankYouConfig {
  title?: string;
  message?: string;
  icon?: 'emoji' | 'checkmark' | 'none';
  buttonLabel?: string;
  buttonUrl?: string;
  redirectUrl?: string;
  redirectDelay?: number;
  showSocialShare?: boolean;
  giftConfig?: BuilderGiftConfig;
}

export interface BuilderExperience {
  presentationMode: 'classic' | 'conversational' | 'compact';
  adaptiveFollowUp: {
    enabled: boolean;
    maxQuestions: number;
    minAnswerLength: number;
    allowedTopics: string[];
    instruction?: string;
  } | null;
}

/** Mirrors QuizConfig in @responsedock/types. */
export interface BuilderQuizConfig {
  showCorrectAnswers: boolean;
  showScore: boolean;
  passingScore?: number;
  allowRetake?: boolean;
}

export interface BuilderSchema {
  pages: BuilderPage[];
  theme: BuilderTheme;
  settings: Record<string, unknown>;
  followUpConfig?: BuilderFollowUpConfig;
  thankYouConfig?: BuilderThankYouConfig;
  experience?: BuilderExperience;
  quizConfig?: BuilderQuizConfig | null;
  estimatedMinutes?: number | null;
  /** The survey's default language; builder strings are stored under this key. Defaults to 'en'. */
  defaultLanguage?: string;
  /** The original persisted schema, without its pages. */
  persisted?: Record<string, unknown>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FALLBACK_LANG = 'en';

let _idCounter = 0;
function uid(): string {
  return `g${Date.now()}_${++_idCounter}`;
}

type Obj = Record<string, unknown>;

function isRecord(val: unknown): val is Obj {
  return !!val && typeof val === 'object' && !Array.isArray(val);
}

function asRecord(val: unknown): Obj {
  return isRecord(val) ? val : {};
}

/** Copy of `obj` without `keys`. */
function omit(obj: unknown, keys: readonly string[]): Obj {
  const out: Obj = {};
  for (const [k, v] of Object.entries(asRecord(obj))) {
    if (!keys.includes(k)) out[k] = v;
  }
  return out;
}

/**
 * Split a TranslationMap (or plain string) into the default-language text and
 * the remaining languages. When the default language is missing (e.g. data
 * saved under 'en' before the survey's language was honoured) the fallback
 * entry becomes the default text and is not repeated as a translation.
 */
function splitText(val: unknown, lang: string): { text: string; others: Record<string, string> } {
  if (val === null || val === undefined || val === '') return { text: '', others: {} };
  if (typeof val === 'string') return { text: val, others: {} };
  if (isRecord(val)) {
    const tm = val as Record<string, string>;
    const key = typeof tm[lang] === 'string'
      ? lang
      : typeof tm[FALLBACK_LANG] === 'string' ? FALLBACK_LANG : Object.keys(tm)[0];
    if (key === undefined) return { text: '', others: {} };
    const { [key]: text, ...others } = tm;
    return { text: typeof text === 'string' ? text : String(text ?? ''), others };
  }
  return { text: String(val), others: {} };
}

/** Extract the default-language string from either a TranslationMap or a plain string */
function getText(val: unknown, lang: string = FALLBACK_LANG): string {
  return splitText(val, lang).text;
}

/** Set the default-language text on a persisted TranslationMap, keeping its other languages. */
function mergeText(source: unknown, lang: string, text: string): Record<string, string> {
  return { [lang]: text, ...splitText(source, lang).others };
}

/** Wrap a string (or pass-through existing TranslationMap) into {[lang]: string} */
function toTM(val: unknown, lang: string = FALLBACK_LANG): Record<string, string> {
  if (!val) return { [lang]: '' };
  if (typeof val === 'string') return { [lang]: val };
  if (typeof val === 'object') return val as Record<string, string>;
  return { [lang]: String(val) };
}

/** Convert an option label to a safe value slug */
function labelToValue(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'option';
}

/** Question config keys the builder edits explicitly; everything else passes through. */
const MODELLED_CONFIG_KEYS = [
  'options', 'min', 'max', 'step', 'minLabel', 'maxLabel', 'npsLowLabel', 'npsHighLabel', 'placeholder',
  'allowOther', 'imageUrl', 'imageAlt', 'maxFiles', 'maxFileSizeMB', 'acceptedFileTypes', 'confidential',
] as const;
const LOCALIZED_CONFIG_KEYS = ['minLabel', 'maxLabel', 'npsLowLabel', 'npsHighLabel', 'placeholder'] as const;
const MODELLED_QUESTION_KEYS = [
  'id', 'blockId', 'type', 'label', 'description', 'required', 'config', 'conditionalLogic',
  'correctAnswer', 'points', 'explanation', 'translations', 'order',
] as const;
const MODELLED_BLOCK_KEYS = ['id', 'type', 'orderIndex', 'config', 'question'] as const;
const MODELLED_PAGE_KEYS = ['id', 'orderIndex', 'order', 'title', 'description', 'isOptional', 'blocks', 'questions'] as const;
const MODELLED_THANK_YOU_KEYS = [
  'title', 'message', 'icon', 'buttonLabel', 'buttonUrl', 'redirectUrl', 'redirectDelay', 'showSocialShare', 'giftConfig',
] as const;
const MODELLED_SCHEMA_KEYS = [
  'pages', 'followUpConfig', 'estimatedMinutes', 'theme', 'thankYouConfig', 'experience', 'quizConfig', 'settings',
] as const;

/** Normalised edit-distance similarity in [0, 1] (1 = identical, case-insensitive). */
function similarity(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x === y) return 1;
  if (!x || !y) return 0;
  let prev = Array.from({ length: y.length + 1 }, (_, j) => j);
  for (let i = 1; i <= x.length; i++) {
    const row = [i];
    for (let j = 1; j <= y.length; j++) {
      row[j] = Math.min(prev[j]! + 1, row[j - 1]! + 1, prev[j - 1]! + (x[i - 1] === y[j - 1] ? 0 : 1));
    }
    prev = row;
  }
  return 1 - prev[y.length]! / Math.max(x.length, y.length);
}

/** Minimum similarity for an edited label to keep the identity of the option it came from. */
const OPTION_RENAME_SIMILARITY = 0.7;

/**
 * Pair the builder's option labels with the options they were loaded from so
 * ids and values stay stable: first by identical label (survives reordering and
 * deletion), then by position (an option renamed in place keeps its value),
 * then by a close label (a typo fix keeps its value after reordering).
 */
function matchPersistedOptions(labels: string[], persisted: Obj[], lang: string): Array<Obj | undefined> {
  const used = new Set<number>();
  const persistedLabels = persisted.map((o) => getText(o['label'], lang).trim());
  const matches: Array<number | undefined> = labels.map((label) => {
    const idx = persistedLabels.findIndex((pl, i) => !used.has(i) && pl === label);
    if (idx === -1) return undefined;
    used.add(idx);
    return idx;
  });
  matches.forEach((match, i) => {
    if (match !== undefined || i >= persisted.length || used.has(i)) return;
    used.add(i);
    matches[i] = i;
  });
  const candidates: Array<{ label: number; option: number; score: number }> = [];
  matches.forEach((match, i) => {
    if (match !== undefined) return;
    persistedLabels.forEach((pl, j) => {
      if (used.has(j)) return;
      const score = similarity(labels[i]!, pl);
      if (score >= OPTION_RENAME_SIMILARITY) candidates.push({ label: i, option: j, score });
    });
  });
  for (const c of candidates.sort((a, b) => b.score - a.score)) {
    if (matches[c.label] !== undefined || used.has(c.option)) continue;
    used.add(c.option);
    matches[c.label] = c.option;
  }
  return matches.map((idx) => (idx === undefined ? undefined : persisted[idx]));
}

function uniqueValue(base: string, taken: Set<string>): string {
  let value = base;
  for (let n = 2; taken.has(value); n++) value = `${base}_${n}`;
  return value;
}

/**
 * Pair each builder option with the persisted option it came from and work out
 * the value it is saved with: the persisted (or explicit) value when there is
 * one, otherwise a unique slug of its label.
 */
function resolveOptions(q: Pick<BuilderQuestion, 'config' | 'persisted'>, lang: string): { matches: Array<Obj | undefined>; values: string[] } {
  const rawOpts = (q.config?.options ?? []) as unknown[];
  const persistedOpts = (q.persisted?.options ?? []).filter(isRecord);
  const stringLabels = rawOpts.map((opt) => (typeof opt === 'string' ? opt.trim() : ''));
  const matches = matchPersistedOptions(stringLabels, persistedOpts, lang);
  const fixedValue = (opt: unknown, i: number): unknown => (typeof opt === 'string' ? matches[i]?.['value'] : asRecord(opt)['value']);
  const takenValues = new Set<string>();
  for (const [i, opt] of rawOpts.entries()) {
    const fixed = fixedValue(opt, i);
    if (typeof fixed === 'string') takenValues.add(fixed);
  }
  const values = rawOpts.map((opt, i) => {
    const fixed = fixedValue(opt, i);
    if (typeof fixed === 'string') return fixed;
    const label = typeof opt === 'string' ? opt.trim() : getText(asRecord(opt)['label'], lang);
    const value = uniqueValue(labelToValue(label) || `opt_${i}`, takenValues);
    takenValues.add(value);
    return value;
  });
  return { matches, values };
}

/**
 * The value each of a builder question's options is saved with (same rules as
 * builderToProper). Answer keys and conditions refer to these values.
 */
export function builderOptionValues(q: Pick<BuilderQuestion, 'config' | 'persisted'>, defaultLanguage?: string): string[] {
  return resolveOptions(q, defaultLanguage ?? FALLBACK_LANG).values;
}

// ─── Builder → Proper ────────────────────────────────────────────────────────

function questionBlockToProper(q: BuilderQuestion, lang: string): Obj {
  const type = q.type as string;
  const persistedBlock = asRecord(q.persisted?.block);
  const persistedQuestion = asRecord(q.persisted?.question);
  const persistedBlockId = typeof persistedBlock['id'] === 'string' ? persistedBlock['id'] : undefined;
  const keepBlockId = !!persistedBlockId
    && (persistedBlockId.replace(/^blk_/, '') === q.id || (type !== 'heading' && type !== 'text' && type !== 'divider' && type !== 'image' && persistedQuestion['id'] === q.id));
  const blockId = keepBlockId ? persistedBlockId! : `blk_${q.id}`;
  const blockExtras = omit(persistedBlock, MODELLED_BLOCK_KEYS);
  const sameType = persistedBlock['type'] === type;

  // Non-question display blocks
  if (type === 'heading' || type === 'text' || type === 'divider') {
    const persistedCfg = sameType ? asRecord(persistedBlock['config']) : {};
    const text = isRecord(persistedCfg['text']) ? mergeText(persistedCfg['text'], lang, q.label) : q.label;
    return {
      ...blockExtras,
      id: blockId,
      type,
      orderIndex: q.order,
      config: {
        ...omit(persistedCfg, ['text', 'format']),
        text,
        ...(type === 'text' && q.config?.format === 'html' ? { format: 'html' } : {}),
      },
    };
  }

  if (type === 'image') {
    const persistedCfg = sameType ? asRecord(persistedBlock['config']) : {};
    const caption = isRecord(persistedCfg['caption']) ? mergeText(persistedCfg['caption'], lang, q.label) : q.label;
    return {
      ...blockExtras,
      id: blockId,
      type,
      orderIndex: q.order,
      config: {
        ...omit(persistedCfg, ['imageUrl', 'imageAlt', 'caption']),
        imageUrl: q.config?.imageUrl ?? '',
        imageAlt: q.config?.imageAlt ?? '',
        caption,
      },
    };
  }

  // Convert options (support both string[] and QuestionOption[])
  const rawOpts = (q.config?.options ?? []) as unknown[];
  const { matches, values } = resolveOptions(q, lang);
  const translatedOption = (i: number): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [optLang, t] of Object.entries(q.translations ?? {})) {
      const translated = t.options?.[i];
      if (translated && optLang !== lang) out[optLang] = translated;
    }
    return out;
  };
  const properOptions = rawOpts.map((opt, i) => {
    const value = values[i]!;
    if (typeof opt === 'string') {
      const label = opt.trim();
      const source = matches[i];
      const sourceId = source?.['id'];
      return {
        ...(source ?? {}),
        id: typeof sourceId === 'string' && sourceId ? sourceId : `opt_${value}`,
        // Builder language map: default lang + translations
        label: { [lang]: label, ...translatedOption(i) },
        value,
        order: i,
      };
    }
    const o = opt as Obj;
    return {
      ...o,
      id: (o['id'] as string) ?? `opt_${value}`,
      label: typeof o['label'] === 'string' ? { [lang]: o['label'], ...translatedOption(i) } : toTM(o['label'], lang),
      value,
      order: (o['order'] as number) ?? i,
    };
  });

  // Build proper question config
  const persistedCfg = asRecord(persistedQuestion['config']);
  const localized = (key: (typeof LOCALIZED_CONFIG_KEYS)[number]): Record<string, string> | undefined => {
    const val = q.config?.[key];
    if (!val) return undefined;
    return typeof val === 'string' ? mergeText(persistedCfg[key], lang, val) : toTM(val, lang);
  };
  const cfg: Obj = {};
  // Unmodelled keys (loaded from the persisted config or set by the builder) pass through
  for (const [k, v] of Object.entries(q.config ?? {})) {
    if (!(MODELLED_CONFIG_KEYS as readonly string[]).includes(k) && v !== undefined) cfg[k] = v;
  }
  if (properOptions.length > 0) cfg['options'] = properOptions;
  // Rating/NPS: ensure min/max always present (fall back to defaults)
  if (type === 'rating') {
    cfg['min'] = q.config?.min ?? 1;
    cfg['max'] = q.config?.max ?? 5;
  } else if (type === 'nps') {
    cfg['min'] = q.config?.min ?? 0;
    cfg['max'] = q.config?.max ?? 10;
  } else {
    if (q.config?.min !== undefined) cfg['min'] = q.config.min;
    if (q.config?.max !== undefined) cfg['max'] = q.config.max;
  }
  if (q.config?.step !== undefined) cfg['step'] = q.config.step;
  for (const key of LOCALIZED_CONFIG_KEYS) {
    const tm = localized(key);
    if (tm) cfg[key] = tm;
  }
  if (q.config?.allowOther !== undefined) cfg['allowOther'] = q.config.allowOther;
  if (q.config?.imageUrl) cfg['imageUrl'] = q.config.imageUrl;
  if (q.config?.imageAlt) cfg['imageAlt'] = q.config.imageAlt;
  if (q.config?.maxFiles !== undefined) cfg['maxFiles'] = q.config.maxFiles;
  if (q.config?.maxFileSizeMB !== undefined) cfg['maxFileSizeMB'] = q.config.maxFileSizeMB;
  if (q.config?.acceptedFileTypes !== undefined) cfg['acceptedFileTypes'] = q.config.acceptedFileTypes;
  if (q.config?.confidential !== undefined) cfg['confidential'] = q.config.confidential;

  const labelTranslations: Record<string, string> = {};
  for (const [labelLang, t] of Object.entries(q.translations ?? {})) {
    if (labelLang !== lang && t.label) labelTranslations[labelLang] = t.label;
  }

  return {
    ...blockExtras,
    id: blockId,
    type: 'question',
    orderIndex: q.order,
    config: asRecord(persistedBlock['config']),
    question: {
      ...omit(persistedQuestion, MODELLED_QUESTION_KEYS),
      id: q.id,
      blockId,
      type,
      label: { [lang]: q.label, ...labelTranslations },
      description: q.description ? mergeText(persistedQuestion['description'], lang, q.description) : null,
      required: q.required,
      config: cfg,
      conditionalLogic: q.conditionalLogic ?? null,
      ...(q.correctAnswer !== undefined ? { correctAnswer: q.correctAnswer } : {}),
      ...(q.points !== undefined ? { points: q.points } : {}),
      ...(q.explanation !== undefined ? { explanation: q.explanation } : {}),
    },
  };
}

/**
 * Convert the builder's simplified schema to the proper API schema.
 * Call this before saving to the API.
 *
 * Builder strings are stored under `defaultLanguage` (falling back to the
 * builder schema's `defaultLanguage`, then 'en').
 */
export function builderToProper(builder: BuilderSchema, defaultLanguage?: string): Record<string, unknown> {
  const lang = defaultLanguage ?? builder.defaultLanguage ?? FALLBACK_LANG;
  const persistedSchema = asRecord(builder.persisted);

  const pages = (builder.pages ?? []).map((page) => {
    const persistedPage = asRecord(page.persisted);
    return {
      ...omit(persistedPage, MODELLED_PAGE_KEYS),
      id: page.id,
      orderIndex: page.order,
      title: mergeText(persistedPage['title'], lang, page.title ?? ''),
      description: page.description ? mergeText(persistedPage['description'], lang, page.description) : null,
      isOptional: typeof persistedPage['isOptional'] === 'boolean' ? persistedPage['isOptional'] : false,
      blocks: (page.questions ?? []).map((q) => questionBlockToProper(q, lang)),
    };
  });

  // Theme
  const t = builder.theme ?? {};
  let properTheme: Obj | null = null;
  if (Object.keys(t).some((k) => t[k as keyof BuilderTheme])) {
    properTheme = {};
    for (const [k, v] of Object.entries({ ...t, primaryColor: t.primaryColor ?? '#06B6D4', buttonStyle: t.buttonStyle ?? 'rounded' })) {
      if (v !== undefined) properTheme[k] = v;
    }
  }

  // FollowUp
  let followUpConfig: Record<string, unknown> | null = null;
  if (builder.followUpConfig) {
    const fuc = builder.followUpConfig;
    const rawFUC = asRecord(persistedSchema['followUpConfig']);
    const rawFields = Array.isArray(rawFUC['fields']) ? (rawFUC['fields'] as unknown[]).filter(isRecord) : [];
    followUpConfig = {
      ...omit(rawFUC, ['enabled', 'title', 'description', 'fields']),
      enabled: fuc.enabled,
      title: mergeText(rawFUC['title'], lang, fuc.title),
      description: mergeText(rawFUC['description'], lang, fuc.description),
      fields: (fuc.fields ?? []).map((f) => {
        const source = rawFields.find((raw) => raw['id'] === f.id);
        return {
          ...omit(source, ['id', 'type', 'label', 'required']),
          id: f.id,
          type: f.type,
          label: mergeText(source?.['label'], lang, f.label),
          required: f.required,
        };
      }),
    };
  }

  // ThankYou
  let thankYouConfig: Record<string, unknown> | null = null;
  if (builder.thankYouConfig) {
    const tyc = builder.thankYouConfig;
    const rawTYC = asRecord(persistedSchema['thankYouConfig']);
    thankYouConfig = {
      ...omit(rawTYC, MODELLED_THANK_YOU_KEYS),
      ...(tyc.title ? { title: mergeText(rawTYC['title'], lang, tyc.title) } : {}),
      ...(tyc.message ? { message: mergeText(rawTYC['message'], lang, tyc.message) } : {}),
      ...(tyc.icon ? { icon: tyc.icon } : {}),
      ...(tyc.buttonLabel ? { buttonLabel: tyc.buttonLabel } : {}),
      ...(tyc.buttonUrl ? { buttonUrl: tyc.buttonUrl } : {}),
      ...(tyc.redirectUrl ? { redirectUrl: tyc.redirectUrl } : {}),
      ...(tyc.redirectDelay !== undefined ? { redirectDelay: tyc.redirectDelay } : {}),
      ...(tyc.showSocialShare !== undefined ? { showSocialShare: tyc.showSocialShare } : {}),
      ...(tyc.giftConfig?.enabled && (tyc.giftConfig.codes ?? []).filter(Boolean).length > 0 ? {
        giftConfig: {
          enabled: true,
          type: tyc.giftConfig.type,
          codes: tyc.giftConfig.codes.filter(Boolean),
          ...(tyc.giftConfig.label ? { label: tyc.giftConfig.label } : {}),
        },
      } : {}),
    };
  }

  const settings = builder.settings ?? {};

  return {
    // Top-level fields the builder does not know about pass through unchanged
    ...omit(persistedSchema, MODELLED_SCHEMA_KEYS),
    pages,
    followUpConfig,
    estimatedMinutes: builder.estimatedMinutes ?? null,
    theme: properTheme,
    thankYouConfig,
    experience: builder.experience ?? { presentationMode: 'classic', adaptiveFollowUp: null },
    ...(builder.quizConfig !== undefined ? { quizConfig: builder.quizConfig } : {}),
    ...(Object.keys(settings).length > 0 ? { settings } : {}),
  };
}

// ─── Proper → Builder ────────────────────────────────────────────────────────

/** Read builder-modelled config keys; unmodelled keys are copied verbatim. */
function configToBuilder(qCfg: Obj, options: string[], lang: string): BuilderQuestionConfig {
  const builderCfg: BuilderQuestionConfig = {};
  for (const [k, v] of Object.entries(qCfg)) {
    if (!(MODELLED_CONFIG_KEYS as readonly string[]).includes(k)) builderCfg[k] = v;
  }
  if (options.length > 0) builderCfg.options = options;
  if (qCfg['min'] !== undefined) builderCfg.min = qCfg['min'] as number;
  if (qCfg['max'] !== undefined) builderCfg.max = qCfg['max'] as number;
  if (qCfg['step'] !== undefined) builderCfg.step = qCfg['step'] as number;
  for (const key of LOCALIZED_CONFIG_KEYS) {
    if (qCfg[key]) builderCfg[key] = getText(qCfg[key], lang);
  }
  if (qCfg['allowOther'] !== undefined) builderCfg.allowOther = qCfg['allowOther'] as boolean;
  if (qCfg['imageUrl']) builderCfg.imageUrl = qCfg['imageUrl'] as string;
  if (qCfg['imageAlt']) builderCfg.imageAlt = qCfg['imageAlt'] as string;
  if (qCfg['maxFiles'] !== undefined) builderCfg.maxFiles = qCfg['maxFiles'] as number;
  if (qCfg['maxFileSizeMB'] !== undefined) builderCfg.maxFileSizeMB = qCfg['maxFileSizeMB'] as number;
  if (Array.isArray(qCfg['acceptedFileTypes'])) builderCfg.acceptedFileTypes = qCfg['acceptedFileTypes'] as string[];
  if (qCfg['confidential'] !== undefined) builderCfg.confidential = qCfg['confidential'] as boolean;
  return builderCfg;
}

/** Non-empty options as builder strings, with the persisted option objects aligned to them. */
function optionsToBuilder(rawOpts: unknown[], lang: string): { labels: string[]; persisted: Obj[] } {
  const labels: string[] = [];
  const persisted: Obj[] = [];
  for (const opt of rawOpts) {
    const label = typeof opt === 'string'
      ? opt
      : getText(asRecord(opt)['label'], lang) || ((asRecord(opt)['value'] as string) ?? '');
    if (!label) continue;
    labels.push(label);
    persisted.push(typeof opt === 'string' ? { label: opt } : asRecord(opt));
  }
  return { labels, persisted };
}

/** Quiz fields present on a persisted question. */
function quizFields(q: Obj): Pick<BuilderQuestion, 'correctAnswer' | 'points' | 'explanation'> {
  return {
    ...(q['correctAnswer'] !== undefined ? { correctAnswer: q['correctAnswer'] } : {}),
    ...(typeof q['points'] === 'number' ? { points: q['points'] } : {}),
    ...(q['explanation'] !== undefined ? { explanation: q['explanation'] as BuilderQuestion['explanation'] } : {}),
  };
}

/**
 * Convert the proper API schema back to the builder's simplified format.
 * Call this when loading a saved version into the builder.
 *
 * `defaultLanguage` is the survey's default language (falls back to 'en');
 * other languages are exposed as question translations or preserved as-is.
 */
export function properToBuilder(schema: Record<string, unknown>, defaultLanguage?: string): BuilderSchema {
  const lang = defaultLanguage ?? FALLBACK_LANG;
  const rawPages = (schema['pages'] ?? []) as unknown[];

  const pages: BuilderPage[] = rawPages.map((page, pageIdx) => {
    const p = page as Record<string, unknown>;
    const pageId = (p['id'] as string) ?? uid();

    let questions: BuilderQuestion[];

    if (Array.isArray(p['blocks'])) {
      // Proper format — convert blocks → questions
      const blocks = [...(p['blocks'] as Record<string, unknown>[])];
      questions = blocks
        .sort((a, b) => ((a['orderIndex'] as number) ?? 0) - ((b['orderIndex'] as number) ?? 0))
        .map((block, blockIdx): BuilderQuestion => {
          const blockType = block['type'] as string;
          const blockId = typeof block['id'] === 'string' ? block['id'] : undefined;
          const persistedBlock = omit(block, ['question']);

          // Display blocks
          if (blockType === 'heading' || blockType === 'text' || blockType === 'divider') {
            const cfg = (block['config'] as Record<string, unknown>) ?? {};
            return {
              id: blockId?.replace(/^blk_/, '') ?? uid(),
              type: blockType,
              order: (block['orderIndex'] as number) ?? blockIdx,
              label: getText(cfg['text'], lang),
              required: false,
              config: blockType === 'text' && cfg['format'] === 'html' ? { format: 'html' } : {},
              persisted: { block: persistedBlock },
            };
          }

          if (blockType === 'image') {
            const cfg = (block['config'] as Record<string, unknown>) ?? {};
            return {
              id: blockId?.replace(/^blk_/, '') ?? uid(),
              type: blockType,
              order: (block['orderIndex'] as number) ?? blockIdx,
              label: getText(cfg['caption'], lang),
              required: false,
              config: {
                imageUrl: (cfg['imageUrl'] as string) ?? '',
                imageAlt: (cfg['imageAlt'] as string) ?? '',
              },
              persisted: { block: persistedBlock },
            };
          }

          // Question block
          const q = (block['question'] as Record<string, unknown>) ?? {};
          const qCfg = (q['config'] as Record<string, unknown>) ?? {};
          const { labels: stringOptions, persisted: persistedOptions } = optionsToBuilder(
            (qCfg['options'] ?? []) as unknown[],
            lang,
          );
          const builderCfg = configToBuilder(qCfg, stringOptions, lang);

          // Extract per-language translations from the label map
          const label = splitText(q['label'], lang);
          const translations: Record<string, { label?: string; options?: string[] }> = {};
          for (const [labelLang, lbl] of Object.entries(label.others)) {
            translations[labelLang] = { label: lbl };
          }
          // Per-language option translations
          persistedOptions.forEach((o, optIdx) => {
            for (const [optLang, lbl] of Object.entries(splitText(o['label'], lang).others)) {
              if (!translations[optLang]) translations[optLang] = {};
              if (!translations[optLang]!.options) translations[optLang]!.options = [];
              translations[optLang]!.options![optIdx] = lbl;
            }
          });

          const description = getText(q['description'], lang);

          return {
            id: (q['id'] as string) ?? uid(),
            type: (q['type'] as string) ?? 'short_text',
            order: (block['orderIndex'] as number) ?? blockIdx,
            label: label.text,
            required: (q['required'] as boolean) ?? false,
            config: builderCfg,
            conditionalLogic: (q['conditionalLogic'] as ConditionalLogic | null | undefined) ?? null,
            ...(description ? { description } : {}),
            ...quizFields(q),
            ...(Object.keys(translations).length > 0 ? { translations } : {}),
            persisted: {
              block: persistedBlock,
              question: q,
              ...(persistedOptions.length > 0 ? { options: persistedOptions } : {}),
            },
          };
        });
    } else if (Array.isArray(p['questions'])) {
      // Already builder format — normalize options (QuestionOption[] → string[])
      questions = (p['questions'] as unknown[]).map((q, qIdx) => {
        const question = q as Record<string, unknown>;
        const cfg = (question['config'] as Record<string, unknown>) ?? {};
        const { labels, persisted: persistedOptions } = optionsToBuilder((cfg['options'] ?? []) as unknown[], lang);
        const description = getText(question['description'], lang);
        const translations = question['translations'] as BuilderQuestion['translations'];

        return {
          id: (question['id'] as string) ?? uid(),
          type: (question['type'] as string) ?? 'short_text',
          order: (question['order'] as number) ?? qIdx,
          label: getText(question['label'], lang),
          required: (question['required'] as boolean) ?? false,
          config: configToBuilder(cfg, labels, lang),
          conditionalLogic: (question['conditionalLogic'] as ConditionalLogic | null | undefined) ?? null,
          ...(description ? { description } : {}),
          ...quizFields(question),
          ...(isRecord(translations) ? { translations } : {}),
          persisted: {
            question: omit(question, ['translations', 'order']),
            ...(persistedOptions.length > 0 ? { options: persistedOptions } : {}),
          },
        };
      });
    } else {
      questions = [];
    }

    const description = getText(p['description'], lang);

    return {
      id: pageId,
      order: ((p['orderIndex'] as number) ?? (p['order'] as number)) ?? pageIdx,
      title: getText(p['title'], lang),
      ...(description ? { description } : {}),
      questions,
      persisted: omit(p, ['blocks', 'questions']),
    };
  });

  // Theme: keep every persisted token, including ones the branding dialog does not edit
  const rawTheme = (schema['theme'] ?? {}) as Record<string, unknown>;
  const theme: BuilderTheme = {};
  for (const [k, v] of Object.entries(rawTheme)) {
    if (v !== undefined && v !== null && v !== '') (theme as Record<string, unknown>)[k] = v;
  }

  // FollowUp
  const rawFUC = schema['followUpConfig'] as Record<string, unknown> | null | undefined;
  let followUpConfig: BuilderFollowUpConfig | undefined;
  if (rawFUC) {
    followUpConfig = {
      enabled: (rawFUC['enabled'] as boolean) ?? false,
      title: getText(rawFUC['title'], lang),
      description: getText(rawFUC['description'], lang),
      fields: ((rawFUC['fields'] ?? []) as unknown[]).map((f) => {
        const field = f as Record<string, unknown>;
        return {
          id: (field['id'] as string) ?? uid(),
          type: (field['type'] as 'name' | 'email' | 'phone') ?? 'email',
          label: getText(field['label'], lang),
          required: (field['required'] as boolean) ?? false,
        };
      }),
    };
  }

  // ThankYou
  const rawTYC = schema['thankYouConfig'] as Record<string, unknown> | null | undefined;
  let thankYouConfig: BuilderThankYouConfig | undefined;
  if (rawTYC) {
    thankYouConfig = {
      title: getText(rawTYC['title'], lang),
      message: getText(rawTYC['message'], lang),
      icon: rawTYC['icon'] as BuilderThankYouConfig['icon'] | undefined,
      buttonLabel: rawTYC['buttonLabel'] as string | undefined,
      buttonUrl: rawTYC['buttonUrl'] as string | undefined,
      redirectUrl: rawTYC['redirectUrl'] as string | undefined,
      redirectDelay: rawTYC['redirectDelay'] as number | undefined,
      showSocialShare: rawTYC['showSocialShare'] as boolean | undefined,
      giftConfig: rawTYC['giftConfig'] ? {
        enabled: (rawTYC['giftConfig'] as Record<string, unknown>)['enabled'] as boolean ?? false,
        type: ((rawTYC['giftConfig'] as Record<string, unknown>)['type'] as 'single' | 'random') ?? 'single',
        codes: ((rawTYC['giftConfig'] as Record<string, unknown>)['codes'] as string[]) ?? [],
        label: (rawTYC['giftConfig'] as Record<string, unknown>)['label'] as string | undefined,
      } : undefined,
    };
  }

  const rawExperience = schema['experience'] as Record<string, unknown> | null | undefined;
  const rawAdaptive = rawExperience?.['adaptiveFollowUp'] as Record<string, unknown> | null | undefined;
  const experience: BuilderExperience = {
    presentationMode: (rawExperience?.['presentationMode'] as BuilderExperience['presentationMode']) ?? 'classic',
    adaptiveFollowUp: rawAdaptive ? {
      enabled: (rawAdaptive['enabled'] as boolean) ?? false,
      maxQuestions: (rawAdaptive['maxQuestions'] as number) ?? 1,
      minAnswerLength: (rawAdaptive['minAnswerLength'] as number) ?? 24,
      allowedTopics: (rawAdaptive['allowedTopics'] as string[]) ?? [],
      instruction: rawAdaptive['instruction'] as string | undefined,
    } : null,
  };

  return {
    pages,
    theme,
    settings: (schema['settings'] as Record<string, unknown>) ?? {},
    ...(followUpConfig ? { followUpConfig } : {}),
    ...(thankYouConfig ? { thankYouConfig } : {}),
    experience,
    ...(schema['quizConfig'] !== undefined ? { quizConfig: schema['quizConfig'] as BuilderQuizConfig | null } : {}),
    ...(typeof schema['estimatedMinutes'] === 'number' ? { estimatedMinutes: schema['estimatedMinutes'] } : {}),
    defaultLanguage: lang,
    persisted: omit(schema, ['pages']),
  };
}
